import * as React from 'react';

import { Block, Col, Row } from 'emotion-jsxstyle';
import { DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { PageDropdownItem } from './PageDropdownItem';

import { ActionButton } from './ActionButton';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { MQ } from '../../Theme';
import { History } from 'history';
import Media from 'react-media';

export interface PageAction {
  /** Control logic if the action should be rendered or not */
  shouldRender?: boolean;
  /** External link */
  href?: string;
  /** External link: stay in same tab (default false) */
  hrefSameTab?: boolean;
  /** Internal react-router link */
  to?: string;
  /** Action click handler */
  onClick?: () => void;
  /** Action label */
  label: string;
  /** Font awesome icon name */
  iconName?: string;
  /** Test ID */
  testId?: string;
}

interface PageProps {
  /** Page title */
  title: string;
  /** List of breadcrumbs */
  breadcrumbs?: {
    /** Breadcrumb label */
    label: string;
    /** Pathname */
    to: string;
  }[];
  /** Inner JSX of the page. */
  children?: any;
  /** Primary page action. This will appear as a primary button on the top right. */
  primaryAction?: PageAction;
  /** Additional primary actions shown next to primary action. */
  additionalPrimaryActions?: PageAction[];
  /** List of secondary page actions. */
  secondaryActions?: PageAction[];
  /** History, for clicking mobile links */
  history?: History;
  /** Test ID */
  testId?: string;
}

export class Page extends React.Component<PageProps> {
  renderPrimaryActions() {
    const { primaryAction, additionalPrimaryActions = [] } = this.props;
    const actions = [primaryAction, ...additionalPrimaryActions].filter(action => !!action && action.shouldRender);
    if (actions.length === 0) {
      return null;
    }

    return (
      <Row alignItems="center">
        {actions.map((action, index) => (
          <Block key={action!.label} marginLeft={index === 0 ? undefined : '0.5rem'}>
            <ActionButton
              label={action!.label}
              href={action!.href}
              sameTab={action!.hrefSameTab}
              to={action!.to}
              onClick={action!.onClick}
              iconName={action!.iconName}
              primary={true}
              data-testid={action!.testId || (index === 0 ? 'pageActionPrimary' : undefined)}
            />
          </Block>
        ))}
      </Row>
    );
  }

  render() {
    const props = this.props;
    return (
      <div className="p-0 p-md-2 p-lg-4" data-testid={props.testId}>
        <Row justifContent="space-between" alignItems="center" className="p-3 px-md-0 pt-md-0 pb-md-3">
          <Col flex="1">
            {props.breadcrumbs &&
              props.breadcrumbs.map((b, i) => (
                <Link className="text-muted" to={b.to} key={'breadcrumb--' + i}>
                  <i className="fa fa-chevron-left" /> {b.label}
                </Link>
              ))}
            <h1 className="h3 d-block my-3 font-weight-bold">{props.title}</h1>
            <Helmet titleTemplate="%s | NKBA Admin">
              <title>{props.title}</title>
            </Helmet>

            <Media query={MQ.LG}>
              {(match: boolean) =>
                !!match ? (
                  <Row justifContent="space-between" alignItems="center" flex="1">
                    {props.secondaryActions &&
                      props.secondaryActions.length > 0 &&
                      props.secondaryActions.map(
                        ({ shouldRender, href, hrefSameTab, to, label, iconName, onClick, testId }, i) =>
                          shouldRender && (
                            <Block marginRight="1rem" key={label}>
                              <ActionButton
                                href={href}
                                sameTab={hrefSameTab}
                                to={to}
                                label={label}
                                iconName={iconName}
                                onClick={onClick}
                                data-testid={testId}
                              />
                            </Block>
                          )
                      )}
                  </Row>
                ) : (
                  <Row justifyContent="space-between" alignItems="center">
                    {props.secondaryActions && (
                      <UncontrolledDropdown>
                        <DropdownToggle caret={true}>Actions</DropdownToggle>
                        <DropdownMenu>
                          {props.secondaryActions.map(
                            (action, i) =>
                              action.shouldRender && (
                                <PageDropdownItem action={action} key={action.label} history={this.props.history} />
                              )
                          )}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    )}
                    {this.renderPrimaryActions()}
                  </Row>
                )
              }
            </Media>
          </Col>
          <Media query={MQ.LG}>{this.renderPrimaryActions()}</Media>
        </Row>
        <Block>{props.children}</Block>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/common/components/layout/Page/Page.tsx